import React, { useEffect, useState } from "react";
import { Link } from 'react-router-dom';

import { FaWhatsapp, FaTimes, FaChevronLeft, FaChevronRight, FaDownload, FaShareAlt } from "react-icons/fa";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import ProductCard from "../shared/ProductCard";
import { Modal } from "react-bootstrap";
import { useProducts } from "../ProductContext";
import { isVideoUrl } from "../cloudinaryConfig";

// Amazon-style Hover Zoom Component (Desktop only)
const AmazonZoom = ({ src, alt, onOpenModal }) => {
  const [isHovering, setIsHovering] = useState(false);
  const [lensPosition, setLensPosition] = useState({ x: 0, y: 0 });
  const [backgroundPosition, setBackgroundPosition] = useState('0% 0%');
  const containerRef = React.useRef(null);

  // Zoom settings
  const LENS_SIZE = 150; // Size of the lens in pixels
  const ZOOM_LEVEL = 2.5; // 250% zoom

  // Check if mobile
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 992);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleMouseMove = (e) => {
    if (isMobile || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Keep lens within bounds
    const lensX = Math.max(LENS_SIZE / 2, Math.min(x, rect.width - LENS_SIZE / 2));
    const lensY = Math.max(LENS_SIZE / 2, Math.min(y, rect.height - LENS_SIZE / 2));

    setLensPosition({ x: lensX - LENS_SIZE / 2, y: lensY - LENS_SIZE / 2 });

    // Calculate background position for zoom preview
    const bgX = (x / rect.width) * 100;
    const bgY = (y / rect.height) * 100;
    setBackgroundPosition(`${bgX}% ${bgY}%`);
  };

  const handleMouseEnter = () => {
    if (!isMobile) setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  const handleClick = () => {
    if (isMobile && onOpenModal) {
      onOpenModal();
    }
  };

  return (
    <div className="amazon-zoom-wrapper w-100">
      {/* Main Image Container */}
      <div
        ref={containerRef}
        className="amazon-zoom-main"
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        style={{ cursor: isMobile ? 'zoom-in' : 'crosshair' }}
      >
        <img src={src} alt={alt} className="amazon-zoom-img" draggable={false} />

        {/* Lens overlay - Desktop only */}
        {isHovering && !isMobile && (
          <div
            className="amazon-zoom-lens"
            style={{
              left: lensPosition.x,
              top: lensPosition.y,
              width: LENS_SIZE,
              height: LENS_SIZE,
            }}
          />
        )}

        {/* Zoom icon hint - Opens modal on click */}
        <div
          className="amazon-zoom-hint d-none d-lg-block"
          onClick={(e) => {
            e.stopPropagation();
            if (onOpenModal) onOpenModal();
          }}
          style={{ cursor: 'pointer', pointerEvents: 'auto' }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            <line x1="11" y1="8" x2="11" y2="14"></line>
            <line x1="8" y1="11" x2="14" y2="11"></line>
          </svg>
        </div>
      </div>

      {/* Zoom Preview - Desktop only, shown on right side */}
      {isHovering && !isMobile && (
        <div
          className="amazon-zoom-preview"
          style={{
            backgroundImage: `url(${src})`,
            backgroundSize: `${ZOOM_LEVEL * 100}%`,
            backgroundPosition: backgroundPosition,
          }}
        />
      )}
    </div>
  );
};

// Keep ZoomableImage for Modal (unchanged behavior)
const ZoomableImage = ({ src, alt }) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const [position, setPosition] = useState({ x: "50%", y: "50%" });

  const handleMouseMove = (e) => {
    if (!isZoomed) return;
    const { left, top, width, height } = e.target.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setPosition({ x: `${x}%`, y: `${y}%` });
  };

  const handleClick = () => {
    setIsZoomed(!isZoomed);
    if (isZoomed) {
      setPosition({ x: "50%", y: "50%" });
    }
  };

  return (
    <div
      className="zoom-container"
      style={{
        overflow: "hidden",
        cursor: isZoomed ? "zoom-out" : "zoom-in",
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
      onMouseMove={handleMouseMove}
      onClick={handleClick}
    >
      <img
        src={src}
        alt={alt}
        className="gallery-main-img-dark"
        style={{
          transform: isZoomed ? "scale(2)" : "scale(1)",
          transformOrigin: `${position.x} ${position.y}`,
          transition: "transform 0.2s ease-out",
          width: "100%",
          height: "100%",
          maxHeight: "85vh",
          objectFit: "contain"
        }}
        draggable={false}
      />
    </div>
  );
};

export default function ProductDetails({ productId }) {
  const [product, setProduct] = useState(null);
  const [mainIdx, setMainIdx] = useState(0);
  const [related, setRelated] = useState([]);
  const { getProductDetailsCache, setProductDetailsData } = useProducts();

  // Modal State
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [modalMainIdx, setModalMainIdx] = useState(0);

  useEffect(() => {
    if (!productId) return;

    // Reset mainIdx only when productId changes
    setMainIdx(0);

    // Always fetch fresh product data from Firestore to get ALL images/videos
    const ref = doc(db, "products", productId);
    getDoc(ref)
      .then((snap) => {
        if (snap.exists()) {
          const productData = { id: snap.id, ...snap.data() };
          setProduct(productData);

          // Check cache for related products
          const cached = getProductDetailsCache(productId);
          if (cached && cached.related && cached.related.length > 0) {
            setRelated(cached.related);
            // Update cache with fresh product data
            setProductDetailsData(productId, {
              product: productData,
              related: cached.related
            });
            return;
          }

          // Fetch related products
          if (productData.category) {
            const col = collection(db, "products");
            const q = query(col, where("category", "==", productData.category));
            getDocs(q)
              .then((relSnap) => {
                const arr = relSnap.docs
                  .map((d) => {
                    const data = d.data();
                    // Only keep first image for related products
                    const images = data.images ?? data.image;
                    const firstImage = Array.isArray(images) ? images[0] : images;
                    return {
                      id: d.id,
                      ...data,
                      images: firstImage ? [firstImage] : [],
                    };
                  })
                  .filter((p) => p.id !== productData.id);
                const relatedProducts = arr.slice(0, 4);
                setRelated(relatedProducts);

                // Save to cache (product + related)
                setProductDetailsData(productId, {
                  product: productData,
                  related: relatedProducts
                });
              })
              .catch(console.error);
          } else {
            // No category - just cache product
            setProductDetailsData(productId, { product: productData, related: [] });
          }
        }
      })
      .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  if (!product) {
    return (
      <div className="product-page-wrapper">
        <div className="breadcrumbs skeleton skeleton-breadcrumbs"></div>
        <div className="product-split-layout ">
          <div className="sticky-gallery">
            <div className="gallery-container">
              {/* Vertical Thumbnails Skeleton */}
              <div className="gallery-thumbs-col">
                {[1, 2, 3, 4].map(i => <div key={i} className="thumb-frame skeleton"></div>)}
              </div>
              {/* Main Image Skeleton */}
              <div className="gallery-main-frame skeleton skeleton-gallery"></div>
            </div>
          </div>
          <div className="product-info-col">
            <div className="skeleton skeleton-title-sm"></div>
            <div className="skeleton skeleton-title-lg"></div>
            <div className="skeleton skeleton-price"></div>
            <div className="skeleton skeleton-text-block"></div>
            <div className="skeleton skeleton-text-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  // Image Logic
  const imagesRaw = product?.images ?? product?.image ?? [];
  const images = Array.isArray(imagesRaw)
    ? imagesRaw
    : imagesRaw
      ? [imagesRaw]
      : [];

  const mainSrc = images.length > 0 ? images[mainIdx] : null;

  // Mobile Slider Navigation
  const handlePrevImage = (e) => {
    e.stopPropagation();
    setMainIdx((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setMainIdx((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  // VISIBLE THUMBNAILS LOGIC
  // We show up to 4 thumbnails. If more than 4, the 4th has an overlay.
  const MAX_THUMBS = 4;
  const showMoreOverlay = images.length > MAX_THUMBS;
  const displayedThumbs = images.slice(0, MAX_THUMBS);
  const remainingCount = images.length - MAX_THUMBS + 1; // +1 because we are hiding the 4th one under overlay effectively

  const handleThumbClick = (index) => {
    if (showMoreOverlay && index === MAX_THUMBS - 1) {
      // Open Modal
      setModalMainIdx(0);
      setShowGalleryModal(true);
    } else {
      setMainIdx(index);
    }
  };

  const openModal = (startIdx = 0) => {
    setModalMainIdx(startIdx);
    setShowGalleryModal(true);
  };

  // Modal Navigation
  const nextModalImage = () => {
    setModalMainIdx((prev) => (prev + 1) % images.length);
  };
  const prevModalImage = () => {
    setModalMainIdx((prev) => (prev - 1 + images.length) % images.length);
  };

  // Price Logic
  const price = Number(product.price) || 0;
  const offerPercent = Number(product.offer) || 0;

  const hasDiscount = offerPercent > 0;
  // Calculate discounted price: price - (price * (offer / 100))
  const finalPrice = hasDiscount ? Math.round(price * (1 - offerPercent / 100)) : price;
  const discountPercent = offerPercent;

  const handleWhatsAppInquiry = () => {
    const text = `Hello, I am interested in *${product.name}*.\nLink: ${window.location.href}`;
    const url = `https://wa.me/919924382499?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.name,
          text: `Check out ${product.name} on Shree Kamdhenu Furniture!`,
          url: window.location.href,
        });
      } catch (error) {
        console.log('Error sharing', error);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      // Construct a simple toast or alert check
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div className="product-page-wrapper">
      <nav className="breadcrumbs-nav">
        <ol className="breadcrumbs-list">
          <li className="breadcrumb-item">
            <Link href="/" className="breadcrumb-link">Home</Link>
          </li>
          <li className="breadcrumb-separator">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </li>
          <li className="breadcrumb-item">
            <Link href={`/collections?category=${encodeURIComponent(product.category)}`} className="breadcrumb-link">
              {product.category}
            </Link>
          </li>
          <li className="breadcrumb-separator">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </li>
          <li className="breadcrumb-item active">
            <span className="breadcrumb-current">{product.name}</span>
          </li>
        </ol>
      </nav>

      <div className="product-split-layout">
        {/* Left: Sticky Gallery with Vertical Thumbnails */}
        <div className="sticky-gallery">
          <div className="gallery-container w-100">
            {/* Vertical Thumbnails Column - Left Side */}
            {images.length > 1 && (
              <div className="gallery-thumbs-col">
                {displayedThumbs.map((src, i) => {
                  const isOverlayThumb = showMoreOverlay && i === MAX_THUMBS - 1;
                  const isVideo = isVideoUrl(src);
                  return (
                    <div
                      key={i}
                      className={`thumb-frame ${i === mainIdx && !isOverlayThumb ? 'active' : ''}`}
                      onClick={() => handleThumbClick(i)}
                      style={{ cursor: 'pointer' }}
                    >
                      {isVideo ? (
                        <video
                          src={src}
                          muted
                          style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
                        />
                      ) : (
                        <img
                          src={src}
                          alt={`View ${i + 1}`}
                          style={{ pointerEvents: 'none' }}
                        />
                      )}
                      {isOverlayThumb && (
                        <div className="thumb-overlay">
                          <span>+{remainingCount}</span>
                        </div>
                      )}
                      {isVideo && !isOverlayThumb && (
                        <div className="video-badge">▶</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Main Image/Video - With Amazon Zoom & Mobile Slider */}
            <div className="gallery-main-wrapper" style={{ position: 'relative', flex: 1 }}>
              {mainSrc ? (
                isVideoUrl(mainSrc) ? (
                  <div className="gallery-main-frame">
                    <video
                      key={mainSrc}
                      src={mainSrc}
                      controls
                      autoPlay
                      muted
                      loop
                      playsInline
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  </div>
                ) : (
                  <AmazonZoom
                    key={mainSrc}
                    src={mainSrc}
                    alt={product.name}
                    onOpenModal={() => openModal(mainIdx)}
                  />
                )
              ) : (
                <div className="gallery-main-frame">
                  <div className="no-image-placeholder">
                    No Image Available
                  </div>
                </div>
              )}

              {/* Mobile Slider Controls (Visible only on mobile via CSS) */}
              {images.length > 1 && (
                <>
                  <button className="mobile-nav-btn prev" onClick={handlePrevImage} aria-label="Previous Image">
                    <FaChevronLeft />
                  </button>
                  <button className="mobile-nav-btn next" onClick={handleNextImage} aria-label="Next Image">
                    <FaChevronRight />
                  </button>
                  <div className="mobile-dots-container">
                    {images.map((_, i) => (
                      <span
                        key={i}
                        className={`mobile-dot ${i === mainIdx ? 'active' : ''}`}
                        onClick={(e) => { e.stopPropagation(); setMainIdx(i); }}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right: Product Info */}
        <div className="product-info-col">
          <div className="product-category-label">{product.category}</div>
          <div className="d-flex justify-content-between align-items-start">
            <h1 className="product-title-large mb-0">{product.name}</h1>
            <button
              className="share-btn-product"
              onClick={handleShare}
              title="Share"
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.2rem',
                color: '#555',
                cursor: 'pointer',
                padding: '4px 8px',
                marginTop: '4px'
              }}
            >
              <FaShareAlt />
            </button>
          </div>

          <div className="product-price-block">
            <div className="price-final">₹{finalPrice.toLocaleString()}</div>
            {hasDiscount && (
              <>
                <div className="price-original">₹{price.toLocaleString()}</div>
                <div className="discount-pill">{discountPercent}% OFF</div>
              </>
            )}
          </div>

          <div className="product-description-text">
            {/* Short Description for quick intro */}
            {product.shortDescription && (
              <p className="short-desc">{product.shortDescription}</p>
            )}
            {/* Long Description removed */}
            {product.description}
          </div>

          {/* Specifications Table */}
          <h4 className="details-heading">Product Details</h4>
          <table className="specs-table premium-specs-table">
            <tbody>
              {product.sku && (
                <tr>
                  <td className="specs-label">SKU</td>
                  <td className="specs-value">{product.sku}</td>
                </tr>
              )}
              {product.size && (
                <tr>
                  <td className="specs-label">Dimensions</td>
                  <td className="specs-value">{product.size}</td>
                </tr>
              )}
              {product.material && (
                <tr>
                  <td className="specs-label">Material</td>
                  <td className="specs-value">{product.material}</td>
                </tr>
              )}

              <tr>
                <td className="specs-label">Availability</td>
                <td className={`specs-value ${product.stock !== false ? 'text-success' : 'text-danger'}`}>
                  {product.stock !== false ? 'In Stock' : 'Out of Stock'}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Call to Action - WhatsApp Inquiry */}
          <div className="whatsapp-inquiry-container">
            <button
              onClick={handleWhatsAppInquiry}
              className="whatsapp-inquiry-btn"
            >
              <FaWhatsapp size={24} />
              Inquire via WhatsApp
            </button>
          </div>
        </div>
      </div>

      {/* Related Products */}
      {related.length > 0 && (
        <div className="related-products-section">
          <h3 className="related-heading">You Might Also Like</h3>
          <div className="product-grid">
            {related.map((r) => (
              <ProductCard key={r.id} product={r} />
            ))}
          </div>
        </div>
      )}

      {/* Gallery Modal - Dark UI Refactor */}
      <Modal
        show={showGalleryModal}
        onHide={() => setShowGalleryModal(false)}
        centered
        size="xl"
        className="gallery-modal-dark"
        backdropClassName="gallery-backdrop-dark"
        fullscreen={true} /* Fullscreen for immersive feel */
      >
        <Modal.Body className="p-0 gallery-modal-body-dark">
          {/* Header: Title & Close Actions */}
          <div className="gallery-header">
            <div className="gallery-header-left">
              <h5 className="gallery-title">{product.name}</h5>
              <span className="gallery-subtitle">Click outside or press ESC to close</span>
            </div>
            <div className="gallery-header-right">
              {/* Download Button */}
              <a href={images[modalMainIdx]} download className="action-btn-dark" title="Download">
                <FaDownload />
              </a>
              <button
                onClick={() => setShowGalleryModal(false)}
                className="gallery-close-btn-dark"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="gallery-main-view-dark">
            {/* Navigation - Left */}
            {images.length > 1 && (
              <button onClick={prevModalImage} className="mobile-nav-btn prev">
                <FaChevronLeft />
              </button>
            )}

            {/* Image/Video Container */}
            <div className="gallery-image-wrapper">
              {isVideoUrl(images[modalMainIdx]) ? (
                <video
                  src={images[modalMainIdx]}
                  controls
                  autoPlay
                  style={{ width: '100%', height: '100%', maxHeight: '85vh', objectFit: 'contain' }}
                />
              ) : (
                <ZoomableImage
                  src={images[modalMainIdx]}
                  alt="Full View"
                />
              )}
            </div>

            {/* Navigation - Right */}
            {images.length > 1 && (
              <button onClick={nextModalImage} className="mobile-nav-btn next">
                <FaChevronRight />
              </button>
            )}
          </div>

          {/* Optional: Minimal Thumbs Overlay at bottom center, seemingly floating */}
          <div className="gallery-thumbs-track-dark">
            {images.map((src, i) => (
              <div
                key={i}
                onClick={() => setModalMainIdx(i)}
                className={`gallery-modal-thumb-dark ${i === modalMainIdx ? 'active' : ''}`}
              >
                {isVideoUrl(src) ? (
                  <>
                    <video src={src} muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    {/* <div className="video-badge-modal">▶</div> */}
                  </>
                ) : (
                  <img src={src} alt={`Thumb ${i}`} />
                )}
              </div>
            ))}
          </div>
        </Modal.Body>
      </Modal>

    </div>
  );
}
